import {
  lazy,
  Suspense,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import type { ErrorComponentProps, ErrorRouteComponent } from "@tanstack/react-router";

/** Visible keyboard focus for interactive controls (buttons, tabs, carousel controls). */
export const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const RELOAD_FLAG = "crs:chunk-reload";

/**
 * A stale HTML document can reference asset filenames that no longer exist after a
 * deploy. When that happens the dynamic import rejects and React.lazy caches the
 * rejection forever, so re-rendering (the router error boundary's "Try again")
 * can never recover. Retry once, then fall back to a single guarded hard reload.
 */
export function retryImport<T>(factory: () => Promise<T>, retries = 1): Promise<T> {
  return factory().catch((error: unknown) => {
    if (retries > 0) {
      return new Promise<T>((resolve, reject) => {
        setTimeout(() => {
          retryImport(factory, retries - 1).then(resolve, reject);
        }, 600);
      });
    }

    if (typeof window !== "undefined") {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === "1";
        if (!alreadyReloaded) window.sessionStorage.setItem(RELOAD_FLAG, "1");
      } catch {
        // sessionStorage unavailable (private mode) - fall through to throwing.
      }
      if (!alreadyReloaded) {
        window.location.reload();
        // Keep the promise pending while the page tears down so no error flashes.
        return new Promise<T>(() => {});
      }
    }

    throw error;
  });
}

/** Clears the guard once a page has successfully rendered. */
export function clearChunkReloadFlag() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // ignore
  }
}

export function RouteSkeleton() {
  return (
    <div className="container-page py-10 md:py-16 animate-pulse" aria-hidden>
      <div className="h-8 w-2/3 max-w-md rounded-lg bg-muted" />
      <div className="mt-4 h-4 w-full max-w-xl rounded bg-muted/70" />
      <div className="mt-2 h-4 w-5/6 max-w-lg rounded bg-muted/70" />
      <div className="mt-8 h-48 md:h-64 w-full rounded-2xl bg-muted/60" />
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <div className="h-28 rounded-xl bg-muted/50" />
        <div className="h-28 rounded-xl bg-muted/50" />
        <div className="h-28 rounded-xl bg-muted/50" />
      </div>
      <span className="sr-only">Loading page content</span>
    </div>
  );
}

export function lazyNamed<P extends Record<string, unknown>>(
  factory: () => Promise<P>,
  exportName: keyof P & string,
) {
  const load = () =>
    retryImport(factory).then((module) => {
      clearChunkReloadFlag();
      return { default: module[exportName] as ComponentType };
    });

  // Recreated on remount-after-error so a genuine retry re-attempts the import
  // instead of replaying React.lazy's cached rejection.
  return function LazyRouteComponent() {
    const [Lazy] = useState(() => lazy(load));
    return (
      <Suspense fallback={<RouteSkeleton />}>
        <Lazy />
      </Suspense>
    );
  };
}

/**
 * The router's `errorComponent` and `defaultErrorComponent` types only accept the
 * lazy-wrapped component shape, but a plain function component is what actually
 * renders at runtime. Wrapping the error UI in `lazy()` would make it suspend on
 * first paint, the last thing you want while already showing an error, so this
 * keeps the component synchronous and satisfies the type in one documented place.
 */
export function asErrorComponent<TError = Error>(
  Component: (props: ErrorComponentProps<TError>) => ReactNode,
): ErrorRouteComponent {
  return Component as unknown as ErrorRouteComponent;
}
